import type { Connection, ConnectionRequest } from './types';

/**
 * The connection screens' copy and pure functions.
 *
 * Everything a test needs to hold lives here rather than in a component body: **there is no DOM
 * renderer in this repository and `apps/web`'s test script matches `.test.ts` only, so a rule
 * buried in JSX is a rule with no test.** `event-discovery/constants.ts` states the convention;
 * this file carries more of the weight than most, because three of these strings are specified
 * word for word.
 *
 * Imports here are relative and nothing reaches for the `@/` alias — the tests import this module
 * directly under `tsx --test`, with no Vite to resolve one.
 */

/* -------------------------------------------------------------------------------------------
 * The list (`MEM-033` AC-1)
 * ---------------------------------------------------------------------------------------- */

/** The prototype's own title for this screen, from the profile's Connections figure. */
export const CONNECTIONS_TITLE = 'Your connections';
export const CONNECTIONS_EYEBROW = 'Connections';

/**
 * The sentence under the heading — `Web App.dc.html` §2.10, verbatim.
 *
 * The 10 September screen heads itself *"Connections"* and this repository's title is *"Your
 * connections"*, which is the phone prototype's own wording for the same screen and is kept: it
 * is the one the Connections figure on the profile leads to, and AC-1's *"the member's full set"*
 * reads better as a possessive. The lead is the prototype's.
 */
export const CONNECTIONS_LEAD = "Everyone you've connected with across your events.";

export const NO_CONNECTIONS_TITLE = 'No connections yet';
export const NO_CONNECTIONS_DESCRIPTION =
  'People you meet at an event — by scanning their code in the room, or by connecting after it — land here, across every event you have been to.';

/* -------------------------------------------------------------------------------------------
 * The header's two controls, and the search they drive (`Web App.dc.html` §2.10)
 * ---------------------------------------------------------------------------------------- */

/**
 * The Requests toggle's two labels and its count, from the prototype's `connReqLabel`.
 *
 * The count badge is the ruled home for the pending-request figure: Sumit, 2026-09-11, answer 1 —
 * a dot on Messages for unread, a dot on Your Events for outstanding follow-ups, and *"the count
 * of pending requests on Connections' Requests control"*. `MEM-042` AC-5 draws the other two;
 * this is the third, and it is a **count** rather than a dot because the screen it opens is a
 * queue whose length is the whole of what the member wants to know.
 */
export const REQUESTS_TOGGLE_SHOW = 'Requests';
export const REQUESTS_TOGGLE_HIDE = 'Hide requests';

export function requestsToggleLabel(open: boolean): string {
  return open ? REQUESTS_TOGGLE_HIDE : REQUESTS_TOGGLE_SHOW;
}

/** The accessible name for the count beside it — the badge itself is a bare number. */
export function pendingRequestsCountLabel(count: number): string {
  return count === 1 ? '1 request waiting' : `${String(count)} requests waiting`;
}

/**
 * The panel's lead, from §2.10 verbatim.
 *
 * Its **heading** is {@link REQUESTS_TITLE}, the one this queue has on every surface that draws
 * it — this panel, the screen at `/app/connections/requests`, and the per-event page. Three
 * constants held the same six characters for a week; see {@link REQUESTS_TITLE} for why that is
 * one constant now.
 */
export const REQUESTS_PANEL_LEAD =
  "People who'd like to connect with you, and where you crossed paths.";

export function pendingRequestsPanelCount(count: number): string {
  return `${String(count)} pending`;
}

/** What the panel says once every request has been answered — §2.10's `connReqEmpty`. */
export const REQUESTS_PANEL_CLEARED = "You've handled every request.";

/**
 * **Is anybody waiting on an answer?** (`MEM-042` AC-7, TW 44159923.)
 *
 * A boolean and not a count, deliberately, and the same shape as `hasUnreadThreads` and
 * `hasOutstandingFollowUps` next door — *"a count is a second place the total is stated and a
 * second place it can be wrong"*. The **count** still exists and is still the Requests control's,
 * a few lines up; what this answers is the navigation's question, which is only *is there
 * something*.
 *
 * `undefined` — the read has not answered yet — is false, so the dot appears when there is
 * something to show it for rather than flickering on an empty first render.
 *
 * The API serves pending requests and nothing else (see `useMyConnectionRequests`), so every row
 * it returns is one somebody is waiting on; there is no status to filter by here, and inventing
 * one would be this file disagreeing with the endpoint about what a request is.
 */
export function hasPendingRequests(
  requests: ReadonlyArray<ConnectionRequest> | undefined,
): boolean {
  return (requests ?? []).length > 0;
}

/** The search pill (`Web App.dc.html` §2.10) — its placeholder and its clear control. */
export const CONNECTIONS_SEARCH_LABEL = 'Search your connections';
export const CONNECTIONS_SEARCH_PLACEHOLDER = 'Search name, role or event…';
export const CONNECTIONS_SEARCH_CLEAR = 'Clear the search';

/** The empty state a query with no match produces — §2.10's `connEmpty`, verbatim. */
export const NO_CONNECTIONS_MATCH_TITLE = 'No connections match';
export const NO_CONNECTIONS_MATCH_DESCRIPTION = 'Try a different name, role or event.';

/**
 * What the search pill filters on — **name, role and event**, which is the prototype's own set.
 *
 * Client-side, over the whole list, because the list is already in hand: `GET /api/connections` is
 * the member's entire network in one read, and a query parameter would put a round trip between
 * every keystroke and a screen that already has the answer. It also keeps the rule testable with
 * no server, which is the reason everything else in this file lives here.
 *
 * Company is included although the placeholder does not name it: *"Partner, Cornerstone"* is one
 * line on the card, and a member typing the half of it they remember should find the person. The
 * comparison is case-insensitive and matches anywhere in the field, because a member searching
 * *"cornerstone"* is not spelling a prefix.
 */
export function matchesConnectionQuery(
  connection: Pick<Connection, 'firstName' | 'lastName' | 'role' | 'company' | 'eventName'>,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return [
    connection.firstName,
    connection.lastName,
    connection.role,
    connection.company,
    connection.eventName,
  ].some((field) => (field ?? '').toLowerCase().includes(needle));
}

/* -------------------------------------------------------------------------------------------
 * The connection card (`Web App.dc.html` §3.15)
 * ---------------------------------------------------------------------------------------- */

/**
 * The card's note button, which is why the list payload carries the note at all.
 *
 * The label depends on whether one exists, so the card has to know — see
 * `apps/api/src/domains/connections/types.ts`'s header for the change that made the reader's own
 * note part of `GET /api/connections`, and why `MEM-033` AC-4 is untouched by it.
 */
export const ADD_NOTE_LABEL = 'Add note';
export const EDIT_NOTE_LABEL = 'Edit note';

export function noteButtonLabel(note: string | null | undefined): string {
  return (note ?? '').trim().length === 0 ? ADD_NOTE_LABEL : EDIT_NOTE_LABEL;
}

/** The accessible name, which must name the person — a grid of *Edit note* buttons otherwise. */
export function noteButtonAccessibleName(input: {
  readonly note: string | null | undefined;
  readonly name: string;
}): string {
  return `${noteButtonLabel(input.note)} about ${input.name}`;
}

/** The note composer's heading — §2.16's *"Note about {name}"*. */
export function noteDialogTitle(name: string): string {
  return `Note about ${name}`;
}

/**
 * The prototype's prompt over the composer's field, and the sentence under it.
 *
 * `MEM-CON-010` fixes the *field's* three strings ({@link NOTE_LABEL},
 * {@link NOTE_PRIVACY_QUALIFIER}, {@link NOTE_PLACEHOLDER}) and they are used unchanged inside the
 * composer. This is the overlay's own framing above them, which the specification does not fix.
 */
export const NOTE_DIALOG_PROMPT = 'What do you want to remember?';

/** The card's event pill, whose accessible name has to say where it goes. */
export function eventPillAccessibleName(eventName: string): string {
  return `${eventName} — open the event`;
}

/**
 * **The row's *Connected* pill is gone**, and this records why rather than leaving a reader of the
 * phone prototype looking for it.
 *
 * It came from `Member App.dc.html`'s single-column list row. The 10 September card (§3.15) has no
 * status pill, and it is right not to: a removed connection is not on the list at all (`MEM-034`
 * AC-2), so every card on the screen was wearing the same badge. The word survives on the
 * **detail**, where it is qualified by how many events the two share ({@link connectedPillLabel}).
 */

/* -------------------------------------------------------------------------------------------
 * The detail and its note (`MEM-033` AC-2, AC-3, AC-5)
 * ---------------------------------------------------------------------------------------- */

/**
 * `MEM-CON-010`'s three strings, **specified word for word** and reproduced exactly.
 *
 * The placeholder is doing work, and `MEM-033`'s own note says so: it tells the member the note is
 * for the things that fade — what you talked about — rather than for contact details the platform
 * already has. The ellipsis is a single `…` character, as specified.
 */
export const NOTE_LABEL = 'Your notes';
export const NOTE_PRIVACY_QUALIFIER = 'Private to you';
export const NOTE_PLACEHOLDER =
  'Jot down what you talked about, what to follow up on, anything you want to remember…';

/** Mirrors `CONNECTION_NOTE_MAX_LENGTH` on the API, so the count runs out where the save does. */
export const NOTE_MAX_LENGTH = 2000;

export const NOTE_SAVE_LABEL = 'Save note';
export const NOTE_SAVING_LABEL = 'Saving…';
export const NOTE_SAVED_NOTICE = 'Saved';
export const NOTE_SAVE_FAILED = 'That did not save. Try again.';

/* -------------------------------------------------------------------------------------------
 * The detail's three right-hand cards (`Web App.dc.html` §2.11)
 * ---------------------------------------------------------------------------------------- */

/**
 * The pill beside the name — §2.11's *"Connected · 1 event together"*.
 *
 * A connection belongs to exactly one event in this build (`MEM-033` AC-6), so the count is
 * always one and the sentence still reads correctly. It is written as a function rather than as a
 * constant because `MEM-057`'s profile connect code makes a connection with **no** event, and a
 * pill reading *"1 event together"* about a scan in a coffee queue would be false — that story
 * passes `0` here and gets the plain word.
 */
export function connectedPillLabel(eventCount: number): string {
  if (eventCount <= 0) return 'Connected';
  return eventCount === 1 ? 'Connected · 1 event together' : `Connected · ${String(eventCount)} events together`;
}

/** §2.11's *About* card, over the bio and the interest pills the payload does not carry. */
export const ABOUT_LABEL = 'About';

/** §2.11's *Where you met*, and the row's own link. */
export const WHERE_YOU_MET_LABEL = 'Where you met';
export const NO_EVENT_ON_THIS_CONNECTION =
  'This connection is not tied to an event we can show you.';

/**
 * `MEM-057` AC-4's *"empty Where you met"*, and it is a **different sentence** from the one above.
 *
 * The two nulls mean different things and a member can tell. *"Not tied to an event we can show
 * you"* is about an event that existed and has gone — it invites the reader to wonder which.
 * A profile-code connection was never at an event at all, and saying so is the only way the member
 * finds out why there will be no follow-up openers for this person on Sunday.
 *
 * The card draws no pill for either (`connection-card.tsx`); it is only the detail, which has a
 * whole section headed *Where you met*, that has to say something.
 */
export const CONNECTED_FROM_A_PROFILE_CODE =
  'You connected by code rather than at an event, so there is nothing to show here.';

export function whereYouMetRowAccessibleName(eventName: string): string {
  return `${eventName} — open the event`;
}

/** When the two of you connected, under the event name on a *Where you met* row. */
export function connectedOnLine(establishedAt: string, reader: string): string | null {
  const at = new Date(establishedAt);
  if (Number.isNaN(at.getTime())) return null;
  try {
    const when = new Intl.DateTimeFormat('en-US', {
      timeZone: reader,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(at);
    return `Connected ${when}`;
  } catch {
    return null;
  }
}

/**
 * `MEM-033` AC-5's section, and the sentence that says what it is for.
 *
 * The heading is the 10 September prototype's — *"Elsewhere"* (§2.11) — replacing *"Where to find
 * them"*, which this screen wrote before the prototype drew the section at all. Presentation is
 * the prototype's (`docs/standards/building-from-designs.md`).
 *
 * ## The sentence changed on 2026-09-12, because what these channels *are* changed
 *
 * It used to read *"H2HSpark has no inbox — reach them where they already are."* That sentence
 * existed because ADR 032 made social channels **the only route** two connected members had to
 * each other, and a member who did not know it would wonder where the message button went.
 *
 * ADR 052 supersedes ADR 032 and `MEM-054` builds the thread, so the sentence is now false in its
 * first clause and misleading in its second: there **is** an inbox, it is three items along the
 * navigation, and the Messages card is at the top of this very page. ADR 052 says what the
 * channels become instead — *"Social channels go back to being a convenience … They stay, on the
 * connection detail, and stop carrying that load"* — and this is that, said to the member. The
 * ruling recorded on `MEM-033` (`rulings-2026-09-11.md`, #37) is explicit that the old sentence
 * *"goes when messaging lands (`MEM-054`)"*.
 */
export const SOCIAL_CHANNELS_LABEL = 'Elsewhere';
export const SOCIAL_CHANNELS_DESCRIPTION =
  'The channels they chose to share with the people they connect with — for reaching them somewhere other than here.';
export const NO_SOCIAL_CHANNELS =
  'They have not added any channels yet. You will see them here if they do.';

/**
 * The section the prototype heads *"What they're working on"*.
 *
 * There is no bio field on a member — `member-registration.ts` says so and `MEM-007` is the story
 * that writes it — so this section has nothing to render and the screen does not draw it. Named
 * here rather than silently omitted, because the prototype shows it and the next person to open
 * the design will look for it.
 */
/**
 * The prototype's "What they're working on" section, named rather than dropped.
 *
 * **It used to say the profile editor would bring it.** The profile editor is here — `MEM-007` is
 * `in-review` at 7/7, My Account has the bio field, and members are writing them. This sentence
 * worded its absence well, naming the capability rather than a ticket, and went false anyway,
 * which is the whole reason for the sweep this was found in: good wording does not keep a promise
 * true, only re-reading it when the thing lands does.
 *
 * What is absent is on this side: the connection payload carries no bio. See
 * `attendee-preview/constants.ts`'s twin of this constant for the wiring gap behind both.
 */
/**
 * **What the About card says about somebody who has written nothing** — and the sentence it
 * replaced, which was about us.
 *
 * `BIO_IS_NOT_BUILT` stood here until 2026-09-12: *"This screen does not carry a member's About
 * paragraph yet."* It was true, and Sumit read it on a real connection and asked the obvious
 * question — *"why are we not showing the about and their interests here like in the prototype?"*
 * The answer was a wiring gap: `MEM-007` shipped the bio, the storage had it, and no port between
 * the profile row and this screen had been widened to carry it. `MEM-033` AC-2 is reworded and
 * `describeConnectedProfile` closes the gap.
 *
 * So the empty state is about **the person** again, which is the only thing an empty state on
 * somebody else's profile should ever be about. A member reading it now learns that this person
 * has not written an About — not that the product has not finished one. The difference matters
 * because the first is information and the second is an apology in the place information goes.
 *
 * It says nothing about interests, deliberately: a member with a bio and no interests, or the
 * reverse, is ordinary, and the card renders whichever halves exist. This sentence appears only
 * when **all three** are absent, which is the one case where the card would otherwise be a
 * heading over nothing.
 */
export const NOTHING_WRITTEN_ABOUT_THEM =
  'They have not written an About yet. What you have of them is what they wrote when you connected.';

/* -------------------------------------------------------------------------------------------
 * Removal (`MEM-034`)
 * ---------------------------------------------------------------------------------------- */

export const DISCONNECT_LABEL = 'Disconnect';

/**
 * What removal actually does, said honestly — and **this is a deliberate departure from the
 * prototype**, which says the opposite.
 *
 * `Member App.dc.html` writes *"Removes them from your connections and clears your notes"* on the
 * detail and *"your private notes will be deleted. This can't be undone"* in the confirmation
 * sheet. Neither is true of what was built, and the disagreement is in the *behaviour* rather
 * than the presentation, so the specification wins (`building-from-designs.md`).
 *
 * `MEM-034` AC-3 requires the **other** member's notes to survive, and `connection.ts` makes
 * removal a status rather than a delete precisely so that AC-2 and AC-3 can both hold — *"a
 * delete would satisfy AC-2 and destroy the evidence AC-3 protects"*. Neither row's note is
 * written. Copy promising a deletion that does not happen would be the worse kind of wrong: a
 * member could rely on it.
 *
 * What is kept from the prototype exactly: the silence — *"they won't be notified"* — which is
 * AC-2, and the reassurance that it is not an announcement.
 */
export const DISCONNECT_HELPER =
  'Removes them from your connections. They will not be notified.';

export function disconnectTitle(firstName: string): string {
  return `Disconnect from ${firstName}?`;
}

export const DISCONNECT_CONFIRMATION =
  'They will be removed from your connections and you will be removed from theirs. Nobody is told, and your notes stay with you.';
export const DISCONNECT_KEEP_LABEL = 'Keep connection';
export const DISCONNECT_CONFIRM_LABEL = 'Disconnect';
export const DISCONNECT_FAILED = 'We could not remove that connection.';

/* -------------------------------------------------------------------------------------------
 * Requests (`MEM-032`)
 * ---------------------------------------------------------------------------------------- */

/**
 * **One heading for one queue**, wherever it is drawn.
 *
 * There are three surfaces onto the member's pending inbound requests — the screen this story
 * built (`MEM-032` AC-6), the per-event page (`MEM-053` AC-6) and the panel on Connections
 * (`Web App.dc.html` §2.10) — and they are the same rows read through the same endpoint. They had
 * three constants holding the same string, which is the thing `CONNECT_LABEL`'s own note warns
 * about a few lines below: *"three copies of a label is how one of them ends up saying something
 * slightly different"*.
 */
export const REQUESTS_TITLE = 'Connection requests';
export const REQUESTS_EYEBROW = 'Connections';
export const NO_REQUESTS_TITLE = 'No requests waiting';
export const NO_REQUESTS_DESCRIPTION =
  'When somebody you have not met asks to connect, their request and their note wait for you here.';

export const ACCEPT_LABEL = 'Accept';
export const DECLINE_LABEL = 'Decline';

/**
 * The status pill on a request card — `Web App.dc.html` §3.10, verbatim for the one state that
 * ever renders.
 *
 * The prototype draws three (*Wants to connect*, *Connected*, *Declined*) because its fixtures keep
 * answered requests in the list. Ours do not: answering removes the row and an answered request is
 * served to nobody, including the member who answered it (`MEM-032` AC-7, and `repository.ts`).
 * So there is one pill, and it says what is true of every card on the screen.
 */
export const WANTS_TO_CONNECT_PILL = 'Wants to connect';

/**
 * **This event's requests** (`MEM-053` AC-6) — the prototype's per-event requests page.
 *
 * `Web App.dc.html` §2.5: a heading, a sentence naming the event, and the same request cards. The
 * global screen at `/app/connections/requests` is unchanged and is what Connections' count opens
 * (`MEM-032` AC-6); this one is the same queue filtered to one evening, which is what the header
 * card's count is already counting. Its heading is {@link REQUESTS_TITLE}, like every other
 * surface onto this queue; what is per-event is the sentence beneath it.
 */
export function eventRequestsSubhead(eventName: string): string {
  return `People who'd like to connect with you at ${eventName}.`;
}

/**
 * The per-event empty state.
 *
 * The prototype's is *"No pending requests right now."* under an `inbox` glyph. The sentence is
 * kept; the glyph is not — a guard caught a mail tray on the global screen and was right to. The
 * reason it gave was ADR 032, which ADR 052 has superseded; the reason that outlives it is that
 * this is a queue of **consent decisions** and `/app/messages` is the inbox (`MEM-055`), so a mail
 * tray here would draw the wrong one of two screens that now both exist. It is the `UserPlus` the
 * Connect action carries.
 */
export const NO_EVENT_REQUESTS_TITLE = 'No pending requests right now';
export const NO_EVENT_REQUESTS_DESCRIPTION =
  'People you meet at this event can ask to connect, and their request lands here.';
export const REQUEST_ACTION_FAILED = 'We could not answer that request.';

/* -------------------------------------------------------------------------------------------
 * The composer (`MEM-032` AC-3, AC-4)
 * ---------------------------------------------------------------------------------------- */

/**
 * The Connect control's two labels, wherever it is drawn.
 *
 * Here rather than beside each surface, because `MEM-032` AC-2 puts the same action on a match
 * card, an attendee profile and the in-event roster, and three copies of a label is how one of
 * them ends up saying something slightly different. `MEM-034` AC-5 makes Connect one of exactly
 * two actions offered on a match, so the word matters.
 *
 * `CONNECT_SENT_LABEL` is the prototype's *"Request sent"* — a local, unpersisted state; see
 * `connect-request-dialog.tsx` on why it does not survive a reload and why that is acceptable.
 */
export const CONNECT_LABEL = 'Connect';
export const CONNECT_SENT_LABEL = 'Request sent';
/**
 * **The control's third state as it was, and as it is now only when there is no thread to open.**
 *
 * `Web App.dc.html` §3.8 and §3.9 label it *Connected*, in the success colours with a
 * `message-circle`, and this was that word (Sumit's ruling 29 of 2026-09-11: *"I connected to
 * priya nair but it still says connect"*). The connections list carries the same word as a
 * **badge**, which states the relationship where nothing is being offered and is unchanged.
 *
 * ## The glyph became a real control on 2026-09-12, and #537's open question is what settled it
 *
 * The note here once ended *"there is no thread to open until `MEM-054`"*, which stopped being
 * true on 2026-09-11: ADR 052 supersedes ADR 032, `MEM-054` built the thread and `MEM-055` built
 * the Messages page. #537 corrected that stale reason and replaced it with a live one — that a
 * match card and an attendee card are **pre-connection** surfaces which happen to be looking at
 * somebody the member has since met, so *"turning it into a second, differently-shaped entry point
 * to the thread is a design question for those screens, not a consequence of messaging existing"*.
 *
 * That was exactly the right question, and it was put to Sumit the same day. He answered: **"yes,
 * wire up message and open the thread."** (ruling 64.) `Web App.dc.html` §2.16 draws that third
 * state as *Message* and always did, so the prototype and the product agree now rather than
 * disagreeing. It is not a second entry point either: `ConnectButton` links to `threadPath`, the
 * one address `connection-card.tsx` and the Messages panel already use, and what opens is the
 * thread dialog `MEM-054` built.
 *
 * So this constant is the **fallback** — what a `connected` relationship that somehow arrived
 * without a thread handle says. The API's `connected-carries-a-thread.test.ts` makes that
 * impossible, and the accurate word is still better than a control that opens nothing.
 */
export const CONNECTED_LABEL = 'Connected';

export const COMPOSER_MESSAGE_LABEL = 'Add a message';
export const COMPOSER_SEND_LABEL = 'Send connection request';
export const COMPOSER_SENDING_LABEL = 'Sending…';
export const COMPOSER_CANCEL_LABEL = 'Cancel';
export const COMPOSER_SENT_LABEL = 'Request sent';
export const COMPOSER_FAILED = 'We could not send that request.';
export const MESSAGE_MAX_LENGTH = 500;

export function composerTitle(firstName: string): string {
  return `Connect with ${firstName}`;
}

/**
 * `MEM-032` AC-3's **consequence of sending**, and `MEM-MAT-010`'s helper line — one sentence,
 * because they are one thing said to one person at one moment.
 *
 * ## The sentence changed on 2026-09-12, and the reason is the one this constant was built for
 *
 * It used to end *"you will each be able to see the channels the other chose to share"*, and
 * nothing else. That was a **deliberate departure from the prototype** under ADR 032: the design
 * reads *"They'll see your profile and this note. You can message freely once they accept."*, and
 * with no in-app messaging in MVP the design's second half described a feature that did not
 * exist — a member would send the request expecting a channel that never opened.
 *
 * ADR 052 supersedes ADR 032 and `MEM-054` builds the thread, so the departure has outlived its
 * reason and the substitute has become the lie it was written to prevent. Accepting a request now
 * opens the pair's thread (`MEM-034` AC-6: the note *"becomes the first message on the pair's
 * thread"*), which is the strongest true thing there is to say to somebody deciding whether to
 * send. The prototype's own promise is therefore restored in substance — *the two of you can
 * message each other* — worded as a consequence of **accepting** rather than of sending, which is
 * the distinction `MEM-032` AC-1 turns on and the one the design's *"freely"* blurs.
 *
 * The channels are still named, demoted to what ADR 052 makes them: *"Social channels go back to
 * being a convenience."* They are no longer the point of accepting, so they no longer lead.
 *
 * Kept unchanged: the prototype's first clause, verbatim, because it is exactly right; and
 * `MEM-032` AC-7's silence, which is the fact a sender most needs before they send.
 */
export const COMPOSER_CONSEQUENCE =
  'They will see your profile and this note. If they accept, the two of you can message each other in H2HSpark, alongside any channels you have each chosen to share — and neither of you is told if they do not.';

/**
 * `MEM-032` AC-4 / `MEM-MAT-010` — the message a request from a match arrives pre-filled with.
 *
 * The prototype's own sentence, with the event name substituted. Pre-filled rather than blank
 * because AC-4 asks for it and because the hardest part of asking a stranger to connect is the
 * first line; editable because it is the member's message and not the platform's.
 *
 * `null` for a person the member is not looking at through a match — an attendee profile is not a
 * match, and pre-filling *"we matched"* there would be a claim the platform cannot support.
 */
export function prefilledMatchMessage(parameters: {
  readonly firstName: string;
  readonly eventName: string | null;
}): string {
  return parameters.eventName === null
    ? `Hi ${parameters.firstName} — we matched, and I'd love to connect.`
    : `Hi ${parameters.firstName} — we matched for ${parameters.eventName} and I'd love to connect.`;
}

/* -------------------------------------------------------------------------------------------
 * Shared presentation
 * ---------------------------------------------------------------------------------------- */

/**
 * "Founder, Rooted Health" — the one line the design draws where the API sends two fields.
 *
 * The same rule `attendee-preview/constants.ts` applies, restated here rather than imported,
 * because the shapes differ: a connection's `lastName` is nullable and an attendee's is not, and
 * a shared helper would have to widen for one caller. The comma is presentation either way.
 */
export function roleAtCompany(person: {
  readonly role: string | null;
  readonly company: string | null;
}): string | null {
  const parts = [person.role, person.company].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/** The person's name as one string, for a heading and for an accessible name. */
export function personName(person: {
  readonly firstName: string;
  readonly lastName: string | null;
}): string {
  return [person.firstName, person.lastName].filter((part) => part !== null).join(' ').trim();
}

/**
 * The initials tile the design draws where a photo would go.
 *
 * There is no profile photo on a member (`MEM-006` is not built). Falls back to one letter when
 * there is no surname, rather than rendering a lone initial and a gap.
 */
export function initialsOf(person: {
  readonly firstName: string;
  readonly lastName: string | null;
}): string {
  const first = person.firstName.trim().charAt(0);
  const last = (person.lastName ?? '').trim().charAt(0);
  return `${first}${last}`.toUpperCase();
}

/** The path to one connection's detail. One derivation, so the list and the card cannot differ. */
export function connectionPath(connection: Pick<Connection, 'connectionRef'>): string {
  return `/app/connections/${encodeURIComponent(connection.connectionRef)}`;
}

export const CONNECTIONS_PATH = '/app/connections';

/**
 * The order a connections list renders in.
 *
 * Newest first — a fact about time rather than a ranking, and the same order the API sends. It is
 * applied again here so a screen holding a cached page still reads correctly, and so the rule is
 * testable without a server.
 */
export function newestFirst<T extends { readonly establishedAt: string }>(
  rows: ReadonlyArray<T>,
): ReadonlyArray<T> {
  return [...rows].sort((left, right) => (left.establishedAt < right.establishedAt ? 1 : -1));
}

/** The same, for requests, which carry a different timestamp. */
export function mostRecentRequestFirst(
  rows: ReadonlyArray<ConnectionRequest>,
): ReadonlyArray<ConnectionRequest> {
  return [...rows].sort((left, right) => (left.receivedAt < right.receivedAt ? 1 : -1));
}
